import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ErrorBoundary, Retry } from '@components/common';
import { getUserProfile } from '@store/common/selectors';
import OverviewDatePicker from './components/datepicker/datepicker';
import DashboardTabs from '../common/Dashboard/DashboardTabs';
import { Wrapper, DashboardPanel, TimelineTitleWrapper } from './styled';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import moment from 'moment-timezone';
import '@conviva/organism-components/styles.css';
import { UserTimeline, ItemRenderer } from '@conviva/organism-components';
import { useGetTimelineEventList } from './utils/queryHooks';
import { Button, Space, Tooltip } from 'antd';
import { useAtom, useAtomValue } from 'jotai';
import {
  selectedEventsAtom,
  highlightedSessionIdsAtom,
  savedUserTimelineStateAtom,
} from './atom';
import track, { CLICK_TIMELINE_CUSTOMIZE_APPLY, perfTrack } from '@src/utils/sensor/track';
import { allDimensionsListSelector } from '@src/store/common/dimensionList/selectors';
import {
  getFlowTimelineEventLabel,
  getFlowTimelineQueryInterval,
  processEventAttributeWithGF,
} from './utils/dataUtils';
import { usePageTitle } from '@src/hooks';
import { InfoCircleOutlined, ShareAltOutlined } from '@ant-design/icons';
// Shared hooks
import { useIds } from './hooks/shared/useIds';
import { useTimelineSummary } from './hooks/shared/useTimelineSummary';
import { useShare } from './hooks/shared/useShare';
import { useIdType } from './hooks/shared/useIdType';
import { useErrors } from './hooks/shared/useErrors';
import { useGlobalFilters } from './hooks/shared/useGlobalFilters';
import { useInterval } from './hooks/shared/useInterval';
import { useEnableNW } from './hooks/shared/useEnableNW';
import { useEventFilter } from './hooks/shared/useEventFilter';
import { useItemList } from './hooks/shared/useItemList';
import { useEnableUserId } from './hooks/shared/useEnableUserId';
import { useFlowTimelineMeta } from './hooks/shared/useFlowTimelineMeta';
// Session-specific hooks
import { useSelectedSession } from './hooks/session/useSelectedSession';
import { useSessionTableData } from './hooks/session/useSessionTable';
import { useCustomize } from './hooks/session/useCustomize';
import { loadUserTimelineState } from '@utils/userTimelineStorage';
import SharingPopover from '@components/common/Sharing/SharingPopover';
import DPISummary from '../viewer-module/AISummary/DPISummary';

interface IProps {
  id: string | number;
  flowId?: string | number;
  viewerId?: string | string[];
  clientId: string;
  interval?: string;
  impacted?: boolean;
  source?: string;
  globalFilters?: any;
  idType?: string;
  eventNames?: string[];
}

const EmptyArray: any[] = [];

export const getClientIdSuffix = () => {
  return (
    <Tooltip title="Multiple Client IDs detected. Select them one by one to see the corresponding sessions.">
      <InfoCircleOutlined style={{ color: 'blue' }} />
    </Tooltip>
  );
};
const AppSessionTimeline: React.FC<IProps> = ({
  id = 'user-timeline',
  flowId,
  viewerId = '',
  clientId,
  interval: defaultInterval,
  impacted = false,
  source,
  globalFilters,
  idType: existedIdType,
  eventNames = [],
}) => {
  const timezone = moment().tz() ?? 'UTC';
  const { customer_id } = useSelector(getUserProfile);
  const selectedTrendMetric = useSelector(selectedMetricSelector);
  const [selectedEvents, setSelectedEvents] = useAtom(selectedEventsAtom);
  const [savedTimelineState, setSavedTimelineState] = useAtom(savedUserTimelineStateAtom);

  const dimensionsList = useSelector(allDimensionsListSelector);
  const { isFlowTimeline, flowMetadata } = useFlowTimelineMeta(flowId);
  const { idType, handleIdSelectChanged } = useIdType({
    flowId,
    viewerId,
    clientId,
    existedIdType,
  });
  const { interval, timeSpan, handleDateChange } = useInterval({ defaultInterval });
  const { nwEnabled, handleNWToggleChange } = useEnableNW();
  const { userIdEnabled, handleUserIdToggleChange } = useEnableUserId();
  const { idGroupData, selectedClientId, selectedViewerId, idLoading, shouldShowUserIdToggle } =
    useIds({
      id,
      flowId,
      flowIdentifier: flowMetadata?.identifier,
      viewerId,
      clientId,
      selectedTrendMetric: isFlowTimeline ? flowMetadata.metrics.init : selectedTrendMetric,
      interval,
      customer_id,
      idType,
      handleIdSelectChanged,
    });
  const { customized, customizeData, transformedCustomizeData, onCustomizedChange } = useCustomize({
    flowMetadata,
    savedMetrics: savedTimelineState?.selectedMetrics,
  });
  const sessionTableFields = customized;

  const { summaryData, summaryLoading, summaryError, isFetchedAfterMount, refetchSummary } =
    useTimelineSummary({
      viewerId: selectedViewerId,
      clientId: selectedClientId,
      interval,
    });

  const sessionTableRes = useSessionTableData({
    viewerId: selectedViewerId,
    clientId: selectedClientId,
    interval,
    eventNames,
    flowMetadata,
    fields: sessionTableFields,
    filter: globalFilters,
    options: customizeData,
    impacted,
    isFlowTimeline,
  });

  const { selectedSession, handleSelectedSessionChange } = useSelectedSession({
    impacted,
    sessionTableData: sessionTableRes.data,
    viewerId: selectedViewerId,
    interval,
    clientId: selectedClientId,
    idGroupData,
  });
  const highlightedSessionIds = useAtomValue(highlightedSessionIdsAtom);

  const {
    data: timelineData,
    isFetching: eventListLoading,
    error: eventListError,
    refetch: refetchEventList,
  } = useGetTimelineEventList(
    selectedViewerId,
    isFlowTimeline
      ? getFlowTimelineQueryInterval(
          selectedSession.eventTimeStampStart,
          selectedSession.eventTimeStampEnd,
        )
      : interval,
    selectedClientId,
    selectedSession.sessionId,
    nwEnabled,
    globalFilters,
    userIdEnabled,
    flowMetadata,
  );

  const { error, renderError } = useErrors({
    summaryError,
    sessionTableError: sessionTableRes.error,
    eventListError,
    refetchSummary,
    refetchSessionTable: sessionTableRes.refetch,
    refetchEventList,
  });

  const {
    visible,
    isFetching: shareLoading,
    url,
    id: shareId,
    openShare,
    handleVisibleChange,
  } = useShare({
    interval,
    clientId: selectedClientId,
    viewerId: selectedViewerId,
    source,
    globalFilters,
    idType,
    eventNames,
    flowId,
  });

  useEffect(() => {
    perfTrack();
  }, [isFetchedAfterMount]);

  // Load saved user timeline state from localStorage on mount
  useEffect(() => {
    const loadedState = loadUserTimelineState();
    if (loadedState) {
      setSavedTimelineState(loadedState);
    }
  }, [setSavedTimelineState]);

  const eventList = timelineData?.eventList ?? EmptyArray;
  const resGF = useGlobalFilters({ impacted, globalFilters, dimensionsList });
  const { eventFilterOn, handleEventFilterOnChange } = useEventFilter({ eventList });
  const { timelineItems } = useItemList({ eventList, selectedSession });

  const resEventList = useMemo(() => {
    return eventList
      .map((item) => {
        const res =
          impacted || (shouldShowUserIdToggle && userIdEnabled)
            ? processEventAttributeWithGF(
                item.eventAttributes ?? {},
                resGF,
                dimensionsList as any[],
                item.eventName,
                eventNames,
              )
            : undefined;

        const flowEvent = isFlowTimeline
          ? getFlowTimelineEventLabel(item.eventName, flowMetadata)
          : undefined;

        return {
          ...item,
          ...res,
          flowEvent,
        };
      })
      ?.filter((item) => !!item);
  }, [
    dimensionsList,
    eventList,
    impacted,
    isFlowTimeline,
    resGF,
    shouldShowUserIdToggle,
    userIdEnabled,
    eventNames,
    flowMetadata,
  ]);

  const itemRenderers: ItemRenderer = useMemo(() => ({}), []);

  const renderHeaderRight = () => {
    return (
      <Space direction="vertical">
        <Space direction="horizontal">
          <OverviewDatePicker dateChangeFn={handleDateChange} selectedTimeSpan={timeSpan} />
          <SharingPopover
            url={url}
            id={shareId}
            loading={shareLoading}
            open={visible}
            handleOpen={handleVisibleChange}
            cid={customer_id}
          >
            <Button
              icon={<ShareAltOutlined />}
              onClick={openShare}
              style={{
                borderRadius: '3px',
                width: '28px',
                height: '28px',
              }}
            />
          </SharingPopover>
        </Space>
      </Space>
    );
  };

  const clickApplyInCustomizeDropdown = (value: string) => {
    track({ event: CLICK_TIMELINE_CUSTOMIZE_APPLY, data: { customized: value } });
  };

  usePageTitle({ text: 'User Timeline' });

  const currentSelectedSession = useMemo(() => {
    if (isFlowTimeline)
      return {
        ...selectedSession,
        totalEvents: eventList?.length ?? 'NA',
      };

    return selectedSession;
  }, [isFlowTimeline, selectedSession, eventList]);

  return (
    <Wrapper>
      <DashboardPanel>
        <DashboardTabs id={id as number} />
      </DashboardPanel>
      <Retry isLoading={idLoading || summaryLoading}>
        <UserTimeline
          pageTitle={isFlowTimeline ? `Flow: ${flowMetadata.name}` : undefined}
          sessionTableTitle={isFlowTimeline ? 'Flow Timeline Index' : undefined}
          timelineData={resEventList}
          errorPlaceholder={error ? renderError() : undefined}
          timelineConfig={{
            timelineItems,
            itemRenderers,
          }}
          timelineLoading={eventListLoading || sessionTableRes.loading}
          idGroupData={idGroupData}
          summaryData={summaryData}
          timelineTitle={
            <TimelineTitleWrapper>
              <span>User Timeline</span>
              <DPISummary
                interval={interval}
                viewerId={selectedViewerId}
                clientId={selectedClientId}
                sessionId={selectedSession.sessionId}
                summaryData={summaryData}
                timelineData={timelineData}
              />
            </TimelineTitleWrapper>
          }
          minWidth={780}
          // session table
          sessionTableData={sessionTableRes.data}
          selectedSession={currentSelectedSession}
          highlightedSessions={highlightedSessionIds}
          onSelectedSessionChange={handleSelectedSessionChange}
          sessionTableLoading={sessionTableRes.loading}
          customizeData={transformedCustomizeData}
          customized={customized}
          onCustomizedChange={onCustomizedChange}
          onApplyClicked={clickApplyInCustomizeDropdown}
          // event list
          headerRightComp={renderHeaderRight()}
          enableNetworkRequest={nwEnabled}
          onNetworkRequestToggleChange={handleNWToggleChange}
          showUserIdToggle={shouldShowUserIdToggle}
          enableUserId={shouldShowUserIdToggle && userIdEnabled}
          onUserIdChange={handleUserIdToggleChange}
          timezone={timezone}
          selectedEvents={selectedEvents}
          onSelectedEventsChange={setSelectedEvents}
          eventFilterOn={eventFilterOn}
          onEventFilterOnChange={handleEventFilterOnChange}
          // impacted
          impacted={impacted}
          flowTimeline={isFlowTimeline}
          globalFilters={resGF}
          impactedType={source === 'emp' ? 'Paths/Funnels' : 'Trends'}
          eventNames={eventNames}
          headerWrapperClassName={isFlowTimeline ? 'flow-user-timeline-header' : undefined}
        />
      </Retry>
    </Wrapper>
  );
};

export default React.memo(AppSessionTimeline);
